// Cute per-city landmark icons for the "Local guides by city" cards.
// Decorative, single-color filled silhouettes keyed by city slug; tinted by the
// consumer via the `color` prop (the card passes the season accent color so the
// cool/warm/transition signal the old dot carried is preserved).
// Mirrors the pattern in components/guides/grass-blades.tsx.
// viewBox is 0 0 24 24; render size defaults to 18px.

import type { ComponentType, ReactNode } from 'react'

export interface CityIconProps {
  color?: string
  size?: number
  className?: string
}

const Frame = ({
  size = 18,
  className,
  children,
}: {
  size?: number
  className?: string
  children: ReactNode
}) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    {children}
  </svg>
)

// Chicago — skyline with the antenna-topped Willis Tower.
const Chicago: ComponentType<CityIconProps> = ({ color = 'currentColor', size, className }) => (
  <Frame size={size} className={className}>
    <g fill={color}>
      <rect x="2.4" y="14.5" width="2.6" height="6.5" rx="0.4" />
      <rect x="5.3" y="11" width="2.8" height="10" rx="0.4" />
      <rect x="8.6" y="7.5" width="3" height="13.5" rx="0.4" />
      <rect x="9.2" y="4.3" width="0.55" height="3.4" />
      <rect x="10.45" y="4.3" width="0.55" height="3.4" />
      <rect x="12.1" y="12.5" width="2.7" height="8.5" rx="0.4" />
      <rect x="15.2" y="9.8" width="3" height="11.2" rx="0.4" />
      <rect x="18.7" y="15" width="2.6" height="6" rx="0.4" />
    </g>
  </Frame>
)

// Atlanta — a peach with stem and leaf.
const Atlanta: ComponentType<CityIconProps> = ({ color = 'currentColor', size, className }) => (
  <Frame size={size} className={className}>
    <g fill={color}>
      <circle cx="12" cy="14.3" r="6.3" />
      <rect x="11.4" y="5.6" width="1.2" height="3.4" rx="0.6" />
      <ellipse cx="15.6" cy="7.4" rx="2.6" ry="1.3" transform="rotate(-35 15.6 7.4)" opacity="0.7" />
    </g>
  </Frame>
)

// Minneapolis — a pine tree over lake ripples (North Central, lakes country).
const Minneapolis: ComponentType<CityIconProps> = ({ color = 'currentColor', size, className }) => (
  <Frame size={size} className={className}>
    <g fill={color}>
      <path d="M12 4.4l3.4 5.2H8.6z" />
      <path d="M12 8.2l4.1 6.1H7.9z" />
      <path d="M12 11.8l4.9 6.6H7.1z" />
      <rect x="11" y="18" width="2" height="2" />
    </g>
    <g fill="none" stroke={color} strokeWidth="1.1" strokeLinecap="round" opacity="0.55">
      <path d="M3.5 21h4.2" />
      <path d="M10.4 21h3.2" />
      <path d="M16.3 21h4.2" />
    </g>
  </Frame>
)

// Dallas — a cowboy hat.
const Dallas: ComponentType<CityIconProps> = ({ color = 'currentColor', size, className }) => (
  <Frame size={size} className={className}>
    <g fill={color}>
      <path d="M8 14.6c-.3-3.3.4-6.9 4-6.9s4.3 3.6 4 6.9c-1.2.5-2.6.7-4 .7s-2.8-.2-4-.7z" />
      <path d="M2.5 15.9c1.6-.4 3.2-.5 4.8-.4 1.3.7 3 1.1 4.7 1.1s3.4-.4 4.7-1.1c1.6-.1 3.2 0 4.8.4-.8 1.5-5 2.7-9.5 2.7s-8.7-1.2-9.5-2.7z" />
    </g>
  </Frame>
)

// Denver — mountain peaks with a sun (Mountain West).
const Denver: ComponentType<CityIconProps> = ({ color = 'currentColor', size, className }) => (
  <Frame size={size} className={className}>
    <g fill={color}>
      <circle cx="18.4" cy="6.6" r="2" opacity="0.5" />
      <path d="M2 19.6L8 9l3.5 5.5L15.5 7l6.5 12.6z" />
    </g>
  </Frame>
)

// Charlotte — a crown (the Queen City).
const Charlotte: ComponentType<CityIconProps> = ({ color = 'currentColor', size, className }) => (
  <Frame size={size} className={className}>
    <g fill={color}>
      <path d="M4.6 17.4 3.4 9.4 7.8 13 12 6.4 16.2 13 20.6 9.4 19.4 17.4Z" />
      <rect x="4.6" y="17.7" width="14.8" height="1.9" rx="0.6" />
      <circle cx="12" cy="6.1" r="1.1" />
      <circle cx="3.5" cy="9.1" r="0.9" />
      <circle cx="20.5" cy="9.1" r="0.9" />
    </g>
  </Frame>
)

// New York — the setback Empire State tower with a spire, flanked by buildings.
const NewYork: ComponentType<CityIconProps> = ({ color = 'currentColor', size, className }) => (
  <Frame size={size} className={className}>
    <g fill={color}>
      <rect x="2.5" y="14" width="3" height="7" rx="0.5" />
      <rect x="18.5" y="13" width="3" height="8" rx="0.5" />
      <path d="M10 21V11h4v10z" />
      <path d="M10.8 11V8h2.4v3z" />
      <path d="M11.3 8V6h1.4v2z" />
      <rect x="11.7" y="2.8" width="0.6" height="3.2" />
    </g>
  </Frame>
)

// Houston — a rocket (Space City).
const Houston: ComponentType<CityIconProps> = ({ color = 'currentColor', size, className }) => (
  <Frame size={size} className={className}>
    <g fill={color}>
      <path d="M12 2.8c2.1 2.1 3.1 5.1 3.1 9.1 0 2.3-.5 4.4-1.1 5.9h-4c-.6-1.5-1.1-3.6-1.1-5.9 0-4 1-7 3.1-9.1z" />
      <path d="M9.1 13.7c-1.6.7-2.7 2.3-3.1 4.3l3.1-1.4z" />
      <path d="M14.9 13.7c1.6.7 2.7 2.3 3.1 4.3l-3.1-1.4z" />
      <circle cx="12" cy="10" r="1.6" opacity="0.45" />
      <path d="M10.5 18.1c.3 1.5.8 2.5 1.5 3.2.7-.7 1.2-1.7 1.5-3.2z" opacity="0.6" />
    </g>
  </Frame>
)

// Memphis — a guitar (Beale Street / music city).
const Memphis: ComponentType<CityIconProps> = ({ color = 'currentColor', size, className }) => (
  <Frame size={size} className={className}>
    <g fill={color}>
      <circle cx="8" cy="13.6" r="3" />
      <circle cx="7.4" cy="17.4" r="3.9" />
      <path d="M19.8 4.6 18.4 3.2 9.2 12.2l1.4 1.4z" />
      <path d="M21 3.4 19.8 4.6 18.8 3.6 20 2.4z" />
    </g>
  </Frame>
)

// Seattle — the Space Needle.
const Seattle: ComponentType<CityIconProps> = ({ color = 'currentColor', size, className }) => (
  <Frame size={size} className={className}>
    <g fill={color}>
      <path d="M8 20 11.6 11 12.4 11 16 20 14.3 20 12 13.8 9.7 20Z" />
      <rect x="11.4" y="9" width="1.2" height="9" />
      <ellipse cx="12" cy="9" rx="4.3" ry="1.6" />
      <rect x="11.6" y="4.6" width="0.8" height="3" />
      <circle cx="12" cy="4.3" r="0.7" />
      <rect x="6.6" y="19.4" width="10.8" height="1.5" rx="0.6" />
    </g>
  </Frame>
)

// Fallback for any unmapped slug — a simple map pin.
export const DefaultCityIcon: ComponentType<CityIconProps> = ({
  color = 'currentColor',
  size,
  className,
}) => (
  <Frame size={size} className={className}>
    <g fill={color}>
      <path d="M12 2.6c-3.9 0-7 3-7 6.9 0 4.9 7 12 7 12s7-7.1 7-12c0-3.9-3.1-6.9-7-6.9z" />
      <circle cx="12" cy="9.2" r="2.3" opacity="0.4" />
    </g>
  </Frame>
)

export const CITY_ICONS: Record<string, ComponentType<CityIconProps>> = {
  'chicago-il': Chicago,
  'atlanta-ga': Atlanta,
  'minneapolis-mn': Minneapolis,
  'dallas-tx': Dallas,
  'denver-co': Denver,
  'charlotte-nc': Charlotte,
  'new-york-ny': NewYork,
  'houston-tx': Houston,
  'memphis-tn': Memphis,
  'seattle-wa': Seattle,
}
